import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { ChoiceOption } from '@/components/ui/control'
import { useCase } from '@/features/cases/api/useCase'
import type { AllocationTargets } from '@/features/finance/components/PaymentAllocationSheet'
import { isRefund } from '@/features/finance/paymentRefusal'
import { formatMoney } from '@/lib/format'
import { unitLabel } from '@/lib/legUnits'
import type { Payment } from '@/types/domain'

/* What one payment can be allocated to, and why the act is refused when it is.
 *
 * Written once for the two places the act is offered: the allocation block
 * under a payment (`PaymentAllocations`, the case's receipts and the Finance
 * page's sheet) and the supplier payments' row acts on «Затраты»
 * (`PaymentActs`, S-11 revision). Two copies of the picker would be two answers
 * about which costs a payment may settle.
 *
 * ── The targets depend on the DIRECTION ──────────────────────────────────────
 *
 * `out` settles this case's ACTUAL cost lines, resolved here out of the case
 * the payment names, because the sheet is a picker over choices rather than a
 * second reader of somebody else's rows. An inbound payment leaves `targets`
 * undefined: money received is allocated in the client payment sheet, which
 * reads the case's receivables with what each still takes (S-21).
 *
 * ── A refund settles nothing here (S-22f) ────────────────────────────────────
 *
 * An outbound payment naming the case's CLIENT is a refund — the client's
 * money, never a supplier payment — and the server refuses it any cost line
 * (422 `targetKind: refund`). So the act is refused FIRST, with that rule's
 * words, and the case's cost lines are not read at all. */
export function useAllocationTargets(
  payment: Payment,
  /** Whether this reader may allocate at all. Without it nothing is read. */
  mayAllocate: boolean,
): {
  /** The sheet's targets for an outbound payment; undefined for an inbound
   *  one, which the client payment sheet allocates. */
  targets: AllocationTargets | undefined
  /** Why the act is refused, as a sentence, or undefined when it is not. */
  blocked: string | undefined
} {
  const { t } = useTranslation()
  const outbound = payment.direction === 'out'
  const refund = isRefund(payment)

  /* The case behind an OUTBOUND payment, for its recorded costs. Read through
     the case's own hook rather than a second endpoint of this feature's own:
     the rows are already keyed at `cases.detail(caseId)`, which every payment
     write invalidates, so each line's served `settlement` is re-read after an
     allocation without anything extra being wired — and the picker drops a
     line once it reads `paid`.

     Not asked at all for an inbound payment — the id is empty, the hook is
     disabled — nor for a reader who may not allocate, whose page has no picker
     to fill, nor for a refund, which no cost line may take. */
  const record = useCase(outbound && mayAllocate && !refund ? payment.caseId : '')

  const expenseOptions = useMemo<ChoiceOption[]>(() => {
    const detail = record.data
    if (!detail) return []

    /* The unit by kind and number — «Фура 01KZ…», «Контейнер MSKU…» — so two
       rate lines on one case can be told apart. It is the only thing on a
       cost line that names the unit, and the case detail is already carrying
       it. */
    const labelOf = new Map(
      detail.legs.flatMap((leg) =>
        (leg.units ?? []).map((unit) => [unit.id, unitLabel(t, unit)] as const),
      ),
    )

    /* ACTUAL lines only. A PLAN is refused by the server outright
       (`ErrExpenseLineIsNotADebt`) — nobody has invoiced it, and cash allocated
       against a forecast would leave the real cost reading as unsettled beside
       it. `actualId` is the id an allocation targets, and its absence is the
       whole of the distinction.

       And not one already PAID (S-4.2): the server derives `settlement` from
       the allocations against the line, pending ones included, and its ceiling
       refuses more against a line paid in full — so offering it would be
       offering a refusal. Only `paid` is dropped: `partly-paid` is still owed,
       and an ABSENT status is "not computed", never "paid". */
    return (detail.money?.expenses ?? [])
      .filter((line) => line.actualId !== undefined && line.settlement !== 'paid')
      .map((line) => {
        const figure = line.actual ?? line.planned
        return {
          value: line.actualId ?? '',
          label: line.description,
          hint: [
            line.unitId ? labelOf.get(line.unitId) : undefined,
            formatMoney(figure.amount, figure.currency),
          ]
            .filter(Boolean)
            .join(' · '),
        }
      })
  }, [record.data, t])

  /* Why the picker is empty, when it is — two different facts. No actual cost
     at all is a debt not yet recorded, and the sheet says to record it. Actual
     costs that are all `paid` are the opposite: telling that reader to record
     what is owed is how a carrier comes to be paid twice. */
  const hasActualCosts = (record.data?.money?.expenses ?? []).some(
    (line) => line.actualId !== undefined,
  )

  /* Refused with a SENTENCE rather than a bare disabled button: each of these
     is a reason the reader can act on rather than a transient state whose cause
     is already on screen. The costs still being read is the one that is
     genuinely transient, and it is stated anyway — the alternative is a sheet
     that opens saying this case has no costs while its read is still in flight,
     which is a lie the reader would act on. */
  const blocked = refund
    ? t('finance.allocations.refundNotCost')
    : Number(payment.unallocated.amount) <= 0
      ? t('finance.allocations.fullyAllocated')
      : outbound && record.isPending
        ? t('finance.allocations.expenseTargetsLoading')
        : outbound && record.isError
          ? t('finance.allocations.expenseTargetsFailed')
          : undefined

  return {
    blocked,
    targets: outbound
      ? {
          kind: 'expense',
          options: expenseOptions,
          notice: t('finance.allocations.expenseTargetsNote'),
          emptyNotice: hasActualCosts
            ? t('finance.allocations.expenseTargetsAllPaid')
            : t('finance.allocations.expenseTargetsNone'),
        }
      : undefined,
  }
}
